import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import Pagination from './Pagination';
import { Button } from './ui/Button';
import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
} from '../styles/designTokens';
import { config } from '../config/environment';
import { IpAllowlistItem } from '../models/CustomerModels';
import { CustomerService } from '../services/CustomerService';
import { formatDateTimeKST } from '../utils/dateUtils';
import { validateCidrClientInput } from '../utils/cidrClientValidate';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const InfoBanner = styled.div<{ $variant?: 'info' | 'warn' }>`
  padding: ${spacing[4]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  line-height: 1.5;
  background: ${({ $variant }) =>
    $variant === 'warn' ? colors.warning[50] : colors.primary[50]};
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'warn' ? colors.warning[200] : colors.primary[200]};
  color: ${colors.semantic.text.secondary};
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  align-items: center;
`;

const TableWrap = styled.div`
  background: ${colors.semantic.background.secondary};
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.semantic.border.primary};
  overflow: auto;
  box-shadow: ${shadows.sm};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 480px;
`;

const Th = styled.th`
  text-align: left;
  padding: ${spacing[3]};
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.tertiary};
  border-bottom: 1px solid ${colors.semantic.border.primary};
  background: ${colors.semantic.background.primary};
`;

const Td = styled.td`
  padding: ${spacing[3]};
  font-size: ${typography.fontSize.sm};
  border-bottom: 1px solid ${colors.secondary[100]};
  color: ${colors.semantic.text.secondary};
`;

const Tr = styled.tr<{ $clickable?: boolean }>`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:hover {
    background: ${({ $clickable }) =>
      $clickable ? colors.semantic.background.primary : 'transparent'};
  }
`;

const AddForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  max-width: 640px;
`;

const InputRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  align-items: flex-start;
`;

const TextInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
`;

const SuccessMsg = styled.div`
  padding: ${spacing[3]};
  background: ${colors.success[50]};
  border: 1px solid ${colors.success[200]};
  color: ${colors.success[800]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
`;

const ErrorMsg = styled.div`
  padding: ${spacing[3]};
  background: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  color: ${colors.error[800]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
`;

const PanelBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1000;
`;

const SidePanel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  width: min(400px, 100vw);
  height: 100vh;
  background: ${colors.semantic.background.primary};
  box-shadow: ${shadows['2xl']};
  z-index: 1001;
  padding: ${spacing[5]};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: ${typography.fontSize.lg};
  color: ${colors.semantic.text.primary};
`;

const ModalBox = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(420px, 92vw);
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[6]};
  z-index: 1100;
  box-shadow: ${shadows['2xl']};
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing[2]};
  margin-top: ${spacing[5]};
`;

const StatusBadge = styled.span<{ $active: boolean }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSize.xs};
  font-weight: 600;
  background: ${({ $active }) => ($active ? colors.success[50] : colors.neutral[100])};
  color: ${({ $active }) => ($active ? colors.success[800] : colors.semantic.text.tertiary)};
`;

export interface CustomerIpAllowlistTabProps {
  userIdx: number;
}

const CustomerIpAllowlistTab: React.FC<CustomerIpAllowlistTabProps> = ({ userIdx }) => {
  const readonlyUi = config.IP_ALLOWLIST_READONLY;

  const [items, setItems] = useState<IpAllowlistItem[]>([]);
  const [allowLoading, setAllowLoading] = useState(false);
  const [allowError, setAllowError] = useState<string | null>(null);
  const [allowPage, setAllowPage] = useState(1);
  const [allowLimit] = useState(20);
  const [allowTotal, setAllowTotal] = useState(0);
  const [allowTotalPages, setAllowTotalPages] = useState(0);
  const [sort, setSort] = useState<'create_asc' | 'create_desc'>('create_asc');

  const [panelRow, setPanelRow] = useState<IpAllowlistItem | null>(null);
  const [deleteRow, setDeleteRow] = useState<IpAllowlistItem | null>(null);
  const [restoreRow, setRestoreRow] = useState<IpAllowlistItem | null>(null);
  const [cidrInput, setCidrInput] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [editingCidr, setEditingCidr] = useState(false);
  const [editCidrValue, setEditCidrValue] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    window.setTimeout(() => setSuccessMsg(null), 4000);
  };

  const loadAllowlist = useCallback(async () => {
    setAllowLoading(true);
    setAllowError(null);
    try {
      const res = await CustomerService.getIpAllowlist(userIdx, {
        page: allowPage,
        limit: allowLimit,
        sort,
        status: 'all',
      });
      setItems(res.items || []);
      setAllowTotal(res.total ?? 0);
      setAllowTotalPages(res.total_pages ?? 0);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'IP 허용 목록을 불러오지 못했습니다.';
      setAllowError(msg);
      setItems([]);
    } finally {
      setAllowLoading(false);
    }
  }, [userIdx, allowPage, allowLimit, sort]);

  useEffect(() => {
    loadAllowlist();
  }, [loadAllowlist]);

  const handleAdd = async () => {
    if (readonlyUi) return;
    setFormError(null);
    const clientErr = validateCidrClientInput(cidrInput);
    if (clientErr) {
      setFormError(clientErr);
      return;
    }
    try {
      await CustomerService.postIpAllowlist(userIdx, cidrInput.trim());
      setCidrInput('');
      showSuccess('IP 허용 항목이 추가되었습니다.');
      await loadAllowlist();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        '추가에 실패했습니다.';
      setFormError(msg);
    }
  };

  const handleSaveEdit = async () => {
    if (!panelRow || readonlyUi) return;
    setEditError(null);
    const err = validateCidrClientInput(editCidrValue);
    if (err) {
      setEditError(err);
      return;
    }
    try {
      const data = await CustomerService.patchIpAllowlist(
        userIdx,
        panelRow.allowlist_idx,
        editCidrValue.trim()
      );
      setEditingCidr(false);
      showSuccess('CIDR이 수정되었습니다.');
      setPanelRow({
        allowlist_idx: data.allowlist_idx,
        user_idx: data.user_idx,
        cidr: data.cidr,
        create_datetime: data.create_datetime,
        delete_datetime: data.delete_datetime,
        is_active: data.is_active,
        range_summary_ko: data.range_summary_ko,
      });
      await loadAllowlist();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        '수정에 실패했습니다.';
      setEditError(msg);
    }
  };

  const confirmDelete = async () => {
    if (!deleteRow || readonlyUi) return;
    try {
      await CustomerService.deleteIpAllowlist(userIdx, deleteRow.allowlist_idx);
      setDeleteRow(null);
      setPanelRow(null);
      showSuccess('항목이 삭제(소프트 삭제)되어 비활성 상태가 되었습니다.');
      await loadAllowlist();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        '삭제(소프트 삭제)에 실패했습니다.';
      setAllowError(msg);
      setDeleteRow(null);
    }
  };

  const confirmRestore = async () => {
    if (!restoreRow || readonlyUi) return;
    try {
      await CustomerService.restoreIpAllowlist(userIdx, restoreRow.allowlist_idx);
      setRestoreRow(null);
      setPanelRow(null);
      showSuccess('삭제(소프트 삭제) 항목이 복구되어 활성화되었습니다.');
      await loadAllowlist();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        '복구에 실패했습니다.';
      setAllowError(msg);
      setRestoreRow(null);
    }
  };

  const activeCount = items.filter((row) => row.is_active).length;

  return (
    <Wrap>
      {readonlyUi && (
        <InfoBanner $variant="warn">
          읽기 전용 모드입니다.
        </InfoBanner>
      )}

      {successMsg && <SuccessMsg>{successMsg}</SuccessMsg>}
      {allowError && <ErrorMsg>{allowError}</ErrorMsg>}

      {activeCount === 0 && !allowLoading && (
        <InfoBanner>
          <strong>활성 CIDR 없음</strong>
        </InfoBanner>
      )}

      {!readonlyUi && (
        <AddForm>
          <label
            htmlFor={`cidr-add-${userIdx}`}
            style={{ fontWeight: 600, fontSize: typography.fontSize.sm }}
          >
            CIDR 추가
          </label>
          <InputRow>
            <TextInput
              id={`cidr-add-${userIdx}`}
              placeholder="예: 203.0.113.10 또는 203.0.113.0/24"
              value={cidrInput}
              onChange={(e) => setCidrInput(e.target.value)}
              autoComplete="off"
            />
            <Button type="button" variant="secondary" onClick={handleAdd}>
              추가
            </Button>
          </InputRow>
          {formError && <ErrorMsg>{formError}</ErrorMsg>}
        </AddForm>
      )}

      <Toolbar>
        <span
          style={{
            fontSize: typography.fontSize.sm,
            color: colors.semantic.text.tertiary,
          }}
        >
          정렬
        </span>
        <Button
          type="button"
          variant={sort === 'create_asc' ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => {
            setSort('create_asc');
            setAllowPage(1);
          }}
        >
          생성일 ↑
        </Button>
        <Button
          type="button"
          variant={sort === 'create_desc' ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => {
            setSort('create_desc');
            setAllowPage(1);
          }}
        >
          생성일 ↓
        </Button>
      </Toolbar>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>CIDR</Th>
              <Th>범위 요약</Th>
              <Th>생성일</Th>
              <Th>상태</Th>
            </tr>
          </thead>
          <tbody>
            {allowLoading && (
              <tr>
                <Td colSpan={5}>불러오는 중…</Td>
              </tr>
            )}
            {!allowLoading &&
              items.map((row) => (
                <Tr
                  key={row.allowlist_idx}
                  $clickable
                  onClick={() => {
                    setEditingCidr(false);
                    setEditCidrValue(row.cidr);
                    setEditError(null);
                    setPanelRow(row);
                  }}
                >
                  <Td>{row.allowlist_idx}</Td>
                  <Td>{row.cidr}</Td>
                  <Td>{row.range_summary_ko}</Td>
                  <Td>
                    {row.create_datetime ? formatDateTimeKST(row.create_datetime) : '—'}
                  </Td>
                  <Td>
                    <StatusBadge $active={row.is_active}>
                      {row.is_active ? '활성' : '삭제'}
                    </StatusBadge>
                  </Td>
                </Tr>
              ))}
            {!allowLoading && items.length === 0 && (
              <tr>
                <Td colSpan={5}>표시할 행이 없습니다.</Td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableWrap>

      {allowTotal > 0 && (
        <Pagination
          currentPage={allowPage}
          totalPages={allowTotalPages || 1}
          total={allowTotal}
          limit={allowLimit}
          onPageChange={(p) => setAllowPage(p)}
        />
      )}

      {panelRow && (
        <>
          <PanelBackdrop onClick={() => { setPanelRow(null); setEditingCidr(false); }} />
          <SidePanel>
            <PanelTitle>허용 항목</PanelTitle>
            <div style={{ fontSize: typography.fontSize.sm }}>
              <div>
                <strong>CIDR</strong>
              </div>
              {editingCidr && panelRow.is_active && !readonlyUi ? (
                <InputRow style={{ marginTop: 8 }}>
                  <TextInput
                    value={editCidrValue}
                    onChange={(e) => setEditCidrValue(e.target.value)}
                    autoComplete="off"
                    aria-label="CIDR 수정"
                  />
                </InputRow>
              ) : (
                <div>{panelRow.cidr}</div>
              )}
            </div>
            <div style={{ fontSize: typography.fontSize.sm }}>
              <div>
                <strong>범위 요약</strong>
              </div>
              <div>{panelRow.range_summary_ko}</div>
            </div>
            <div style={{ fontSize: typography.fontSize.sm }}>
              <div>
                <strong>생성일</strong>
              </div>
              <div>
                {panelRow.create_datetime
                  ? formatDateTimeKST(panelRow.create_datetime)
                  : '—'}
              </div>
            </div>
            <div style={{ fontSize: typography.fontSize.sm }}>
              <div>
                <strong>상태</strong>
              </div>
              <div>
                {panelRow.delete_datetime
                  ? `삭제 · ${formatDateTimeKST(panelRow.delete_datetime)}`
                  : '활성'}
              </div>
            </div>
            {editError && <ErrorMsg>{editError}</ErrorMsg>}
            <div style={{ marginTop: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPanelRow(null);
                  setEditingCidr(false);
                }}
              >
                닫기
              </Button>
              {!readonlyUi && panelRow.is_active && !editingCidr && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setEditCidrValue(panelRow.cidr);
                      setEditError(null);
                      setEditingCidr(true);
                    }}
                  >
                    CIDR 수정
                  </Button>
                  <Button type="button" variant="danger" onClick={() => setDeleteRow(panelRow)}>
                    삭제
                  </Button>
                </>
              )}
              {!readonlyUi && !panelRow.is_active && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setRestoreRow(panelRow)}
                >
                  복구
                </Button>
              )}
              {!readonlyUi && panelRow.is_active && editingCidr && (
                <>
                  <Button type="button" variant="outline" onClick={() => setEditingCidr(false)}>
                    취소
                  </Button>
                  <Button type="button" variant="secondary" onClick={handleSaveEdit}>
                    저장
                  </Button>
                </>
              )}
            </div>
          </SidePanel>
        </>
      )}

      {deleteRow && (
        <>
          <PanelBackdrop onClick={() => setDeleteRow(null)} />
          <ModalBox role="dialog" aria-modal="true" aria-labelledby="ip-del-title">
            <h2 id="ip-del-title" style={{ margin: '0 0 12px', fontSize: typography.fontSize.lg }}>
              IP 허용 항목 삭제
            </h2>
            <p style={{ margin: 0, fontSize: typography.fontSize.sm, lineHeight: 1.5 }}>
              <strong>{deleteRow.cidr}</strong> 항목을 삭제(소프트 삭제)하여 비활성화합니다.
              이후 해당 IP 대역에서 JWT API 접근이 <strong>차단될 수 있습니다</strong>.
            </p>
            <ModalActions>
              <Button type="button" variant="outline" onClick={() => setDeleteRow(null)}>
                취소
              </Button>
              <Button type="button" variant="danger" onClick={confirmDelete}>
                삭제
              </Button>
            </ModalActions>
          </ModalBox>
        </>
      )}

      {restoreRow && (
        <>
          <PanelBackdrop onClick={() => setRestoreRow(null)} />
          <ModalBox role="dialog" aria-modal="true" aria-labelledby="ip-restore-title">
            <h2 id="ip-restore-title" style={{ margin: '0 0 12px', fontSize: typography.fontSize.lg }}>
              삭제 항목 복구
            </h2>
            <p style={{ margin: 0, fontSize: typography.fontSize.sm, lineHeight: 1.5 }}>
              <strong>{restoreRow.cidr}</strong> 항목을 복구(활성화)합니다. 복구 후 해당 IP 대역은 다시 허용 목록에 포함됩니다.
            </p>
            <ModalActions>
              <Button type="button" variant="outline" onClick={() => setRestoreRow(null)}>
                취소
              </Button>
              <Button type="button" variant="secondary" onClick={confirmRestore}>
                복구
              </Button>
            </ModalActions>
          </ModalBox>
        </>
      )}
    </Wrap>
  );
};

export default CustomerIpAllowlistTab;
